import React, { forwardRef, useImperativeHandle } from 'react';
import { Input } from './input';
import { loadGoogleMapsApi } from '@/utils/googleMaps';

interface PlaceAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  defaultValue?: string;
}

export const PlaceAutocomplete = forwardRef<HTMLInputElement, PlaceAutocompleteProps>((
  {
  onPlaceSelect,
  placeholder = "Start typing your address...",
  required = false,
  className = "",
    disabled = false,
    defaultValue = ''
  },
  ref
) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let autocomplete: google.maps.places.Autocomplete | null = null;

    const initAutocomplete = async () => {
      try {
        await loadGoogleMapsApi();
        if (!inputRef.current) return;

        autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: "au" },
          fields: ["address_components", "formatted_address", "geometry", "name"],
          types: ["address"]
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete?.getPlace();
          
          if (place) {
            onPlaceSelect(place);
            inputRef.current?.blur();
          }
        });

        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing Autocomplete:', err);
        setIsLoading(false);
      }
    };

    initAutocomplete();

    return () => {
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [onPlaceSelect]);

  return (
    <Input
      ref={inputRef}
      type="text"
      defaultValue={defaultValue}
      placeholder={isLoading ? "Loading address search..." : placeholder}
      required={required}
      className={className}
      disabled={disabled || isLoading}
      autoComplete="off"
    />
  );
});

PlaceAutocomplete.displayName = 'PlaceAutocomplete'; 